import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '@/App';
import SidebarShell from '@/components/wom/SidebarShell';
import { moduleIcon, moduleColor } from '@/lib/moduleIdentity';
import {
  LayoutDashboard, ClipboardCheck, Boxes,
  Truck, FileBarChart2, MapPinned,
  Wallet, Network, UserCog, Users, UsersRound, Building2, Wrench, BadgeCheck,
  GraduationCap, BookOpen, FolderClosed, Library,
} from 'lucide-react';
import OperationsOverview from '@/components/operations/hub/OperationsOverview';
import OperationsInspections from '@/components/operations/hub/OperationsInspections';
import OperationsCorrectiveActions from '@/components/operations/hub/OperationsCorrectiveActions';
import OperationsPlaceholder from '@/components/operations/hub/OperationsPlaceholder';
import OperationsReports from '@/components/operations/hub/OperationsReports';
import OperationsServiceVisits from '@/components/operations/hub/OperationsServiceVisits';
import OperationsTeamInspections from '@/components/operations/hub/OperationsTeamInspections';
import OperationsVehicles from '@/components/operations/hub/OperationsVehicles';
import RoutePlanning from '@/components/operations/RoutePlanning';
import OperationsDirectory from '@/components/operations/opshub/OperationsDirectory';
import PersonnelHub from '@/components/personnel/PersonnelHub';
import OpsBudget from '@/components/operations/opshub/OpsBudget';
import OpsReports from '@/components/operations/opshub/OpsReports';
import OpsTraining from '@/components/operations/opshub/OpsTraining';
import OpsCompanyGuidelines from '@/components/operations/opshub/OpsCompanyGuidelines';
import OpsSafetyDataSheets from '@/components/operations/opshub/OpsSafetyDataSheets';
import { canOps } from '@/components/operations/opshub/opsApi';

// Data sections that render the shared OperationsDirectory at a specific tab (so the
// authoritative directory data is reused — no duplicate screens).
const DIR_OF = {
  customers: 'customers', 'service-locations': 'locations',
  'service-providers': 'providers', assignments: 'assignments',
};

const OpsBrand = () => (
  <div className="flex flex-col items-center border-b border-white/10 bg-white px-4 pt-5 pb-4 text-center" data-testid="ops-brand">
    <img src={moduleIcon('tfs_ops')} alt="TFS Operations Hub" className="w-[68%] max-w-[132px] h-auto object-contain" data-testid="ops-brand-icon" />
    <div className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0E2A47]">Operations Hub</div>
  </div>
);

// Compact in-page sub-tabs used to consolidate related screens under one sidebar entry.
const SubTabs = ({ tabs, active, onPick }) => (
  <div className="flex flex-wrap gap-2 border-b border-slate-200 bg-white px-4 py-3 sm:px-6" data-testid="ops-subtabs">
    {tabs.map((t) => (
      <button key={t.key} onClick={() => onPick(t.key)} data-testid={`ops-subtab-${t.key}`}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${active === t.key ? 'bg-[#0E2A47] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
        {t.label}
      </button>
    ))}
  </div>
);

// Inspections consolidates Inspections, Field (TEAM) inspections, PMP and
// Corrective Actions / POA — each remains its existing screen (no rebuild).
const InspectionsSection = ({ filter, deepPev }) => {
  const [t, setT] = useState('inspections');
  useEffect(() => { if (deepPev) setT('inspections'); }, [deepPev]);
  return (
    <div data-testid="ops-inspections-section">
      <SubTabs
        tabs={[
          { key: 'inspections', label: 'Inspections' },
          { key: 'field', label: 'Field Inspections' },
          { key: 'pmp', label: 'PMP' },
          { key: 'corrective', label: 'Corrective / POA' },
        ]}
        active={t} onPick={setT}
      />
      {t === 'inspections' && <OperationsInspections key={deepPev ? `pev-${deepPev}` : (filter || 'insp')} initialFilter={filter} deepOpenNumber={deepPev} />}
      {t === 'field' && <OperationsTeamInspections />}
      {t === 'pmp' && <OperationsPlaceholder module="preventive-maintenance" />}
      {t === 'corrective' && <OperationsCorrectiveActions key={filter || 'ca'} initialFilter={filter} />}
    </div>
  );
};

// Reports consolidates operational/personnel reports and field & inspection reports.
const ReportsSection = () => {
  const [t, setT] = useState('operational');
  return (
    <div data-testid="ops-reports-section">
      <SubTabs
        tabs={[
          { key: 'operational', label: 'Operational & Personnel' },
          { key: 'field', label: 'Field & Inspection' },
        ]}
        active={t} onPick={setT}
      />
      {t === 'operational' && <OpsReports />}
      {t === 'field' && <OperationsReports />}
    </div>
  );
};

function TfsOperationsHub() {
  const { user } = useContext(AuthContext);
  const [section, setSection] = useState('overview');
  const [filter, setFilter] = useState(null);
  const [deepPev, setDeepPev] = useState(null);

  // Cross-hub deep link: /tfs-ops?pev=<number> opens that Property Evaluation directly.
  useEffect(() => {
    const pev = new URLSearchParams(window.location.search).get('pev');
    if (pev) { setSection('inspections'); setDeepPev(pev); }
  }, []);

  const go = (key, f = null) => {
    // Preserve existing deep links: older callers may still target consolidated keys.
    if (key === 'field-inspections' || key === 'preventive-maintenance') { setSection('inspections'); setFilter(f); return; }
    if (key === 'corrective-actions') { setSection('inspections'); setFilter(f); return; }
    if (key === 'incidents') { setSection('personnel'); setFilter(f); return; }
    if (key === 'ops-reports' || key === 'field-reports') { setSection('reports'); setFilter(f); return; }
    if (key === 'directory') { setSection('customers'); setFilter(f); return; }
    setSection(key); setFilter(f);
  };

  // --- Approved Operations Hub navigation (collapsible groups + standalone items) ---
  const show = (cap) => !cap || canOps(user, cap);
  const RES_CAP = 'ops.personnel.view';   // Training & Resources requires personnel-level authorization

  const customersChildren = [
    { key: 'customers', label: 'Customers', icon: Users, cap: 'ops.customers.view' },
    { key: 'service-locations', label: 'Service Locations', icon: Building2, cap: 'ops.locations.view' },
  ].filter((c) => show(c.cap));

  const teamChildren = [
    { key: 'personnel', label: 'Personnel', icon: UserCog, cap: 'ops.personnel.view' },
    { key: 'service-providers', label: 'Service Providers', icon: Wrench, cap: 'ops.providers.view' },
    { key: 'assignments', label: 'Assignments', icon: Network, cap: 'ops.assignments.view' },
    { key: 'service-verification', label: 'Check-Ins', icon: BadgeCheck },
    { key: 'route-planning', label: 'Route Planning', icon: MapPinned },
  ].filter((c) => show(c.cap));

  const resourcesChildren = [
    { key: 'training', label: 'Training', icon: GraduationCap, cap: RES_CAP },
    { key: 'company-guidelines', label: 'Company Guidelines', icon: BookOpen, cap: RES_CAP },
    { key: 'safety-data-sheets', label: 'Safety Data Sheets', icon: FolderClosed, cap: RES_CAP },
  ].filter((c) => show(c.cap));

  const navItems = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    customersChildren.length && { group: true, key: 'grp-customers', label: 'Customers', icon: Users, children: customersChildren },
    teamChildren.length && { group: true, key: 'grp-team', label: 'Team', icon: UsersRound, children: teamChildren },
    resourcesChildren.length && { group: true, key: 'grp-resources', label: 'Training & Resources', icon: Library, children: resourcesChildren },
    show('ops.budget.view') && { key: 'ops-budget', label: 'Budget', icon: Wallet },
    { key: 'inspections', label: 'Inspections', icon: ClipboardCheck },
    { key: 'vehicles', label: 'Vehicles', icon: Truck },
    { key: 'inventory', label: 'Inventory', icon: Boxes },
    show('ops.reports.view') && { key: 'reports', label: 'Reports', icon: FileBarChart2 },
  ].filter(Boolean);

  // Restricted directory users (no field-ops overview) land on their first permitted
  // data section rather than an empty overview.
  useEffect(() => {
    const isPriv = user?.role === 'owner' || (user?.capabilities || []).includes('admin.controls');
    const dataLeafKeys = [...customersChildren, ...teamChildren.filter((c) => c.cap)].map((c) => c.key);
    if (!isPriv && dataLeafKeys.length > 0) {
      const hasFieldOps = (user?.capabilities || []).some((c) => c.startsWith('operations.') || c === 'inspections.view' || c === 'field.supervisor');
      if (!hasFieldOps) setSection(dataLeafKeys[0]);
    }
    // eslint-disable-next-line
  }, []);

  const dirTab = DIR_OF[section];

  return (
    <div data-testid="tfs-operations-hub">
      <SidebarShell
        navItems={navItems}
        activeKey={section}
        onNavigate={(key) => go(key)}
        brandNode={<OpsBrand />}
        mobileBrand={<img src={moduleIcon('tfs_ops')} alt="Operations Hub" className="h-8 w-8 object-contain" data-testid="ops-mobile-brand" />}
        workspaceLabel="Operations Hub"
        logoutTo="/login"
        mobileNavGrid
        mobileNavAccent={moduleColor('tfs_ops')}
        navAccent={moduleColor('tfs_ops')}
      >
        {section === 'overview' && <OperationsOverview onOpen={go} />}
        {dirTab && <OperationsDirectory key={dirTab} initialTab={dirTab} />}
        {section === 'personnel' && <PersonnelHub embedded />}
        {section === 'ops-budget' && <OpsBudget />}
        {section === 'training' && <OpsTraining />}
        {section === 'company-guidelines' && <OpsCompanyGuidelines />}
        {section === 'safety-data-sheets' && <OpsSafetyDataSheets />}
        {section === 'inspections' && <InspectionsSection filter={filter} deepPev={deepPev} />}
        {section === 'service-verification' && <OperationsServiceVisits />}
        {section === 'route-planning' && <RoutePlanning />}
        {section === 'inventory' && <OperationsPlaceholder module="inventory" />}
        {section === 'vehicles' && <OperationsVehicles />}
        {section === 'reports' && <ReportsSection />}
      </SidebarShell>
    </div>
  );
}

export default TfsOperationsHub;
